import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import commissionGroupsService from '../../common/api/commissionGroupsService';
import { LOADING_STATUS } from '../../common/constants';

export interface Commission {
  id: string;
  temporaryId: string;
  commissionPercent: number;
  activeFromDate: Date;
}

export interface CommissionGroupDetails {
  id: string;
  name: string;
  siteId: string;
  commissions: Commission[];
}

interface CommissionGroupDetailsState {
  details: CommissionGroupDetails | null;
  status: LOADING_STATUS.IDLE | LOADING_STATUS.LOADING | LOADING_STATUS.FAILED;
}

export const initialCommissionGroup: CommissionGroupDetails = {
  id: '',
  name: '',
  siteId: '7bf9e2e4-8567-4ef4-84ee-e38b23b72499',
  commissions: [],
};

export const initialState: CommissionGroupDetailsState = {
  details: null,
  status: LOADING_STATUS.IDLE,
};

export const getCommissionGroupDetails = createAsyncThunk(
  'commissionGroupDetails/getCommissionGroupDetails',
  async (commissionGroupId: string, { rejectWithValue }) => {
    try {
      return await commissionGroupsService.fetchCommissionGroupDetails(
        commissionGroupId,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveOrUpdateCommissionGroup = createAsyncThunk(
  'commissionGroupDetails/saveOrUpdateCommission',
  async (commissionData: CommissionGroupDetails, { rejectWithValue }) => {
    try {
      return await commissionGroupsService.saveOrUpdateCommissionGroup(
        commissionData,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const commissionGroupDetailsSlice = createSlice({
  name: 'commissionGroupDetails',
  initialState,
  reducers: {
    updateCommissionGroupDetails: (
      state,
      action: PayloadAction<CommissionGroupDetails>,
    ) => {
      state.details = action.payload;
    },
    updateCommission: (
      state,
      action: PayloadAction<{ commission: Commission }>,
    ) => {
      const { commission } = action.payload;

      let index = state.details.commissions.findIndex(
        c => commission.id && c.id === commission.id,
      );

      if (index === -1) {
        // if the commission is not found by id, try to find it by temporaryId which is used for newly added commissions
        index = state.details.commissions.findIndex(
          c => c.temporaryId === commission.temporaryId,
        );
      }

      if (index !== -1) {
        // update existing commission in the list
        state.details.commissions[index] = commission;
      } else {
        // add new commission to the list
        state.details.commissions.push(commission);
      }
    },
    deleteCommission: (
      state,
      action: PayloadAction<{ commissionId: string }>,
    ) => {
      const { commissionId } = action.payload;

      state.details.commissions = state.details.commissions.filter(
        c => c.id !== commissionId && c.temporaryId !== commissionId,
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getCommissionGroupDetails.pending, state => {
        state.status = LOADING_STATUS.LOADING;
      })
      .addCase(getCommissionGroupDetails.fulfilled, (state, action) => {
        state.status = LOADING_STATUS.IDLE;
        state.details = action.payload;
      })
      .addCase(getCommissionGroupDetails.rejected, (state, action) => {
        state.status = LOADING_STATUS.FAILED;
      });
  },
});

export const {
  updateCommissionGroupDetails,
  updateCommission,
  deleteCommission,
} = commissionGroupDetailsSlice.actions;

export const selectCommissionGroupDetails = (state: RootState) =>
  state.commissionGroup.details;

export const selectCommissionGroupDetailsStatus = (state: RootState) =>
  state.commissionGroup.status;

// selector for fetching commissions with correct order
export const selectOrderedCommissions = createSelector(
  selectCommissionGroupDetails,
  details => {
    if (details && details.commissions.length > 0) {
      // sort the commissions array based on activeFromDate
      return details.commissions.slice().sort((a, b) => {
        return (
          new Date(b.activeFromDate).getTime() -
          new Date(a.activeFromDate).getTime()
        );
      });
    }
    return [];
  },
);

export default commissionGroupDetailsSlice.reducer;
